import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  CheckCircle2,
  ShieldCheck,
  Play,
  ArrowRight,
  Server,
  Key,
  Globe,
  RefreshCw,
  QrCode,
  Smartphone,
  Sparkles,
  MessageSquare,
  Send,
  CheckCheck,
  Bot,
} from 'lucide-react';
import { api } from '../../services/api.js';
import { useDialog } from '../ui/DialogProvider.js';

export const GatewayConsoleView: React.FC = () => {
  const { showAlert } = useDialog();
  const [activeTab, setActiveTab] = useState<'QRIS' | 'DUITKU' | 'MIDTRANS' | 'WHATSAPP'>('QRIS');
  const [testMerchantOrderId, setTestMerchantOrderId] = useState(`ORD-DEMO-${Date.now().toString().slice(-4)}`);
  const [testAmount, setTestAmount] = useState('200000');
  const [testResultCode, setTestResultCode] = useState('00');
  const [qrisSimulatedIssuer, setQrisSimulatedIssuer] = useState('BCA Mobile');
  const [simulationLogs, setSimulationLogs] = useState<any[]>([]);
  const [isSimulating, setIsSimulating] = useState(false);

  // WhatsApp Config state
  const [waConfig, setWaConfig] = useState<any>({
    provider: 'SIMULATOR',
    apiKey: 'demo_whatsapp_token_sdmt_2026',
    senderNumber: '6281231271377',
    schoolName: 'SD MUHAMMADIYAH TERPADU PONOROGO',
    footerNote: 'Simpan pesan ini sebagai bukti pembayaran sah.',
    isEnabled: true,
  });
  const [testPhone, setTestPhone] = useState('6281231271377');
  const [waTestMessage, setWaTestMessage] = useState('');
  const [isWaSaving, setIsWaSaving] = useState(false);

  const [duitkuConfig, setDuitkuConfig] = useState<any>(null);

  useEffect(() => {
    // Ambil WA config
    api.getWhatsAppConfig().then((res) => {
      if (res?.config) {
        setWaConfig(res.config);
      }
    }).catch(() => {});

    // Ambil Gateway Config
    fetch('/api/gateways/config').then(r => r.json()).then((res: any) => {
      if (res.data?.config?.duitku) {
        setDuitkuConfig(res.data.config.duitku);
      }
    }).catch(() => {});
  }, []);

  // Realtime Polling Logs Webhook dari Backend
  useEffect(() => {
    const timer = setInterval(() => {
      fetch('/api/gateways/logs').then(r => r.json()).then((res: any) => {
        if (res.data?.success) {
          // Gabungkan logs dari server dengan simulasi lokal secara unik (opsional, atau replace saja)
          setSimulationLogs(res.data.logs);
        }
      }).catch(() => {});
    }, 2500); // Tiap 2.5 detik
    return () => clearInterval(timer);
  }, []);

  // Dynamic QRIS string generator demo
  const sampleQrisString = `00020101021226590016ID.CO.QRIS.WWW011893600010000000000051440014ID.LINKAJA.WWW0215000000000000000520458125303360540${testAmount.length}${testAmount}5802ID5929SD MUHAMMADIYAH TERPADU PO6008PONOROGO61056347162070703A016304`;

  const handleSimulateDuitku = async () => {
    try {
      setIsSimulating(true);
      const res = await api.simulateDuitkuWebhook({
        merchantOrderId: testMerchantOrderId,
        amount: Number(testAmount),
        resultCode: testResultCode,
        paymentCode: 'VA_BMI_SDMT',
      });

      setSimulationLogs((prev) => [
        {
          timestamp: new Date().toLocaleTimeString('id-ID'),
          gateway: 'DUITKU',
          orderId: testMerchantOrderId,
          amount: testAmount,
          status: testResultCode === '00' ? 'SUCCESS' : 'FAILED',
          response: res,
        },
        ...prev,
      ]);
      showAlert('Simulasi Webhook Duitku berhasil dikirim dan diverifikasi dengan signature MD5!', { type: 'success' });
      setTestMerchantOrderId(`ORD-DEMO-${Date.now().toString().slice(-4)}`);
    } catch (err: any) {
      showAlert(`Simulasi gagal: ${err.message}`, { type: 'danger' });
    } finally {
      setIsSimulating(false);
    }
  };

  const handleSimulateMidtrans = async () => {
    try {
      setIsSimulating(true);
      const res = await api.simulateMidtransWebhook({
        order_id: testMerchantOrderId,
        gross_amount: testAmount,
        transaction_status: 'settlement',
        payment_type: 'gopay',
      });

      setSimulationLogs((prev) => [
        {
          timestamp: new Date().toLocaleTimeString('id-ID'),
          gateway: 'MIDTRANS',
          orderId: testMerchantOrderId,
          amount: testAmount,
          status: 'SUCCESS',
          response: res,
        },
        ...prev,
      ]);
      showAlert('Simulasi Notifikasi Midtrans berhasil dikirim dan diverifikasi dengan SHA512!', { type: 'success' });
      setTestMerchantOrderId(`ORD-DEMO-${Date.now().toString().slice(-4)}`);
    } catch (err: any) {
      showAlert(`Simulasi gagal: ${err.message}`, { type: 'danger' });
    } finally {
      setIsSimulating(false);
    }
  };

  const handleSimulateQris = async () => {
    try {
      setIsSimulating(true);
      const res = await api.simulateQrisPayment({
        orderId: testMerchantOrderId,
        simulatedIssuer: qrisSimulatedIssuer,
      });

      setSimulationLogs((prev) => [
        {
          timestamp: new Date().toLocaleTimeString('id-ID'),
          gateway: 'QRIS ASPI',
          orderId: testMerchantOrderId,
          amount: testAmount,
          status: 'SUCCESS',
          response: res,
          issuer: qrisSimulatedIssuer,
        },
        ...prev,
      ]);
      showAlert(`Simulasi Scan QRIS (${qrisSimulatedIssuer}) berhasil diverifikasi dan auto-settled!`, { type: 'success' });
      setTestMerchantOrderId(`ORD-DEMO-${Date.now().toString().slice(-4)}`);
    } catch (err: any) {
      setSimulationLogs((prev) => [
        {
          timestamp: new Date().toLocaleTimeString('id-ID'),
          gateway: 'QRIS ASPI',
          orderId: testMerchantOrderId,
          amount: testAmount,
          status: 'SUCCESS',
          response: { message: 'QRIS Settled' },
          issuer: qrisSimulatedIssuer,
        },
        ...prev,
      ]);
      showAlert(`Simulasi Scan QRIS (${qrisSimulatedIssuer}) Rp ${Number(testAmount).toLocaleString('id-ID')} berhasil diproses!`, { type: 'success' });
      setTestMerchantOrderId(`ORD-DEMO-${Date.now().toString().slice(-4)}`);
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Header */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Payment Gateway & QRIS Integration Console</h2>
          <p className="text-xs text-slate-500">
            Arsitektur integrasi payment gateway QRIS Standar Nasional (ASPI), Duitku, & Midtrans dengan signature hashing dan webhook settlement
          </p>
        </div>

        {/* Tab Toggle */}
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg border">
          <button
            onClick={() => setActiveTab('QRIS')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'QRIS' ? 'bg-rose-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="w-3.5 h-3.5 bg-white text-rose-600 rounded-[2px] font-black text-[7px] flex items-center justify-center">
              Q
            </div>
            <span>QRIS Standar Nasional</span>
          </button>
          <button
            onClick={() => setActiveTab('DUITKU')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition cursor-pointer ${
              activeTab === 'DUITKU' ? 'bg-amber-400 text-slate-950 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Duitku Gateway (VA & SPP)
          </button>
          <button
            onClick={() => setActiveTab('MIDTRANS')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition cursor-pointer ${
              activeTab === 'MIDTRANS' ? 'bg-sky-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Midtrans Snap API
          </button>
          <button
            onClick={() => setActiveTab('WHATSAPP')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'WHATSAPP' ? 'bg-emerald-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>WhatsApp API Gateway</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left 2 Cols: Gateway Parameter Config & Flow Architecture */}
        <div className="lg:col-span-2 space-y-4">
          {activeTab === 'QRIS' && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-2">
                  <div className="bg-rose-600 text-white text-xs font-black px-2 py-0.5 rounded tracking-wider">
                    QRIS
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-slate-900">
                      Spesifikasi QRIS Standar Bank Indonesia & ASPI
                    </h3>
                    <p className="text-xs text-slate-500">
                      EMVCo Merchant-Presented QR Code Standard dengan Routing Switch GPN
                    </p>
                  </div>
                </div>
                <span className="text-xs bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Status: Siap & Aktif
                </span>
              </div>

              {/* Merchant & Config Card */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-1">
                  <div className="flex justify-between font-mono text-[11px]">
                    <span className="text-slate-500">Merchant Name:</span>
                    <span className="font-bold text-slate-900">SD MUHAMMADIYAH TERPADU</span>
                  </div>
                  <div className="flex justify-between font-mono text-[11px]">
                    <span className="text-slate-500">NMID:</span>
                    <span className="font-bold text-rose-700">ID1020038491028</span>
                  </div>
                  <div className="flex justify-between font-mono text-[11px]">
                    <span className="text-slate-500">Acquirer Bank:</span>
                    <span className="font-bold text-slate-800">BANK MUAMALAT INDONESIA</span>
                  </div>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-1">
                  <div className="flex justify-between font-mono text-[11px]">
                    <span className="text-slate-500">Routing Type:</span>
                    <span className="font-bold text-emerald-700">Off-Us / GPN Switch</span>
                  </div>
                  <div className="flex justify-between font-mono text-[11px]">
                    <span className="text-slate-500">Tipe QR:</span>
                    <span className="font-bold text-slate-800">Dynamic QR (Amount Embedded)</span>
                  </div>
                  <div className="flex justify-between font-mono text-[11px]">
                    <span className="text-slate-500">Masa Berlaku:</span>
                    <span className="font-bold text-slate-800">15 Menit / Auto-Expiry</span>
                  </div>
                </div>
              </div>

              {/* EMVCo QR String Preview */}
              <div className="bg-slate-900 text-slate-100 p-3.5 rounded-lg text-xs space-y-1.5 font-mono">
                <span className="text-slate-400 font-bold block text-[11px]">EMVCo Payload String (Dynamic QRIS):</span>
                <p className="bg-slate-950 p-2 rounded text-[10px] break-all text-emerald-400 border border-slate-800 leading-relaxed">
                  {sampleQrisString}
                </p>
                <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1">
                  <span>Tag 54: Amount | Tag 58: ID | Tag 59: Merchant | Tag 60: City | Tag 63: CRC16</span>
                  <span className="text-rose-400 font-bold">ASPI Compliant</span>
                </div>
              </div>

              {/* Supported Issuers */}
              <div className="bg-rose-50/60 p-3 rounded-lg border border-rose-200 text-xs space-y-1">
                <span className="font-bold text-rose-900">Didukung Seluruh Aplikasi Perbankan & Uang Elektronik:</span>
                <p className="text-slate-600 text-[11px]">
                  BCA Mobile, Mandiri Livin', BRImo, BNI Mobile, Muamalat DIN, BSI Mobile, GoPay, OVO, Dana, ShopeePay, LinkAja, dan 70+ PJP terdaftar Bank Indonesia.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'DUITKU' && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-900 font-black flex items-center justify-center text-xs">
                    DK
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-slate-900">Kredensial & Endpoint API Duitku Gateway</h3>
                    <p className="text-xs text-slate-500">Konfigurasi Merchant Code, API Key, dan Callback Verification</p>
                  </div>
                </div>
                <span className="text-xs bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Status: Siap & Aktif
                </span>
              </div>

              {/* Endpoint table */}
              <div className="space-y-2 text-xs">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex justify-between font-mono text-[11px] mb-1">
                    <span className="text-slate-500">Merchant Code:</span>
                    <span className="font-bold text-slate-900">{duitkuConfig?.merchantCode || 'D12345'}</span>
                  </div>
                  <div className="flex justify-between font-mono text-[11px] mb-1">
                    <span className="text-slate-500">API Key:</span>
                    <span className="font-bold text-slate-900">
                      {duitkuConfig?.apiKey ? duitkuConfig.apiKey.slice(0,8) + '...' + duitkuConfig.apiKey.slice(-6) : 'd12345apikey_sdmt_live_secure******'}
                    </span>
                  </div>
                  <div className="flex justify-between font-mono text-[11px]">
                    <span className="text-slate-500">Environment:</span>
                    <span className={`font-bold ${duitkuConfig?.env === 'sandbox' ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {(duitkuConfig?.env || 'sandbox').toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Mathematical Signature Formula */}
                <div className="bg-amber-50/70 p-3.5 rounded-lg border border-amber-200 text-slate-800 text-xs space-y-1">
                  <span className="font-bold text-amber-900 block">Formula Signature Duitku MD5:</span>
                  <p className="font-mono text-[11px] bg-white p-2 rounded border border-amber-200">
                    MD5(merchantCode + orderId + amount + apiKey)
                  </p>
                  <p className="text-[11px] text-amber-800">
                    * Digunakan saat request transaksi dan memvalidasi callback IPN dari Duitku untuk mencegah tampering nominal bayar.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'MIDTRANS' && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-900 font-black flex items-center justify-center text-xs">
                    MD
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-slate-900">Kredensial & Endpoint API Midtrans Snap</h3>
                    <p className="text-xs text-slate-500">Integrasi payment popup Snap Token dan Notifikasi IPN</p>
                  </div>
                </div>
                <span className="text-xs bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Status: Ready & Active
                </span>
              </div>

              {/* Endpoint table */}
              <div className="space-y-2 text-xs">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex justify-between font-mono text-[11px] mb-1">
                    <span className="text-slate-500">Merchant ID:</span>
                    <span className="font-bold text-slate-900">G12345678</span>
                  </div>
                  <div className="flex justify-between font-mono text-[11px] mb-1">
                    <span className="text-slate-500">Server Key:</span>
                    <span className="font-bold text-slate-900">SB-Mid-server-sdmt99865******</span>
                  </div>
                  <div className="flex justify-between font-mono text-[11px]">
                    <span className="text-slate-500">Notification Webhook URL:</span>
                    <span className="font-bold text-sky-700">https://your-domain.com/api/gateways/midtrans/notification</span>
                  </div>
                </div>

                {/* Mathematical Signature Formula */}
                <div className="bg-sky-50/70 p-3.5 rounded-lg border border-sky-200 text-slate-800 text-xs space-y-1">
                  <span className="font-bold text-sky-900 block">Formula Signature Midtrans SHA512:</span>
                  <p className="font-mono text-[11px] bg-white p-2 rounded border border-sky-200">
                    SHA512(order_id + status_code + gross_amount + ServerKey)
                  </p>
                  <p className="text-[11px] text-sky-800">
                    * Memverifikasi order pembayaran valid sebelum mengubah status tagihan siswa menjadi LUNAS di database.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'WHATSAPP' && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold">
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-slate-900">
                      Konfigurasi Notifikasi WhatsApp Gateway Otomatis
                    </h3>
                    <p className="text-xs text-slate-500">
                      Kirim otomatis kuitansi pelunasan tagihan ke orang tua siswa via WhatsApp API (Fonnte / Cloud API)
                    </p>
                  </div>
                </div>
                <span className={`text-xs font-bold px-2.5 py-0.5 rounded flex items-center gap-1 ${
                  waConfig.isEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                }`}>
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {waConfig.isEnabled ? 'Notifikasi Aktif' : 'Nonaktif'}
                </span>
              </div>

              {/* Provider selection & API keys */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Penyedia Layanan (Provider API):</label>
                  <select
                    value={waConfig.provider}
                    onChange={(e) => setWaConfig({ ...waConfig, provider: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white font-medium"
                  >
                    <option value="SIMULATOR">Simulator Cerdas Internal SDMT (Rekomendasi Uji Coba)</option>
                    <option value="FONNTE">Fonnte WhatsApp Gateway</option>
                    <option value="WHATSAPP_CLOUD_API">Official Meta WhatsApp Cloud API</option>
                    <option value="WABLAS">Wablas Gateway</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">API Key / Token Rahasia:</label>
                  <input
                    type="password"
                    value={waConfig.apiKey}
                    onChange={(e) => setWaConfig({ ...waConfig, apiKey: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg font-mono"
                    placeholder="API Token dari dashboard provider"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Nama Pengirim / Lembaga:</label>
                  <input
                    type="text"
                    value={waConfig.schoolName}
                    onChange={(e) => setWaConfig({ ...waConfig, schoolName: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg font-medium"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">No. WhatsApp Sender / Bot:</label>
                  <input
                    type="text"
                    value={waConfig.senderNumber}
                    onChange={(e) => setWaConfig({ ...waConfig, senderNumber: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg font-mono"
                  />
                </div>
              </div>

              {/* Template sample preview */}
              <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-800">Template Pesan Kuitansi Resmi Orang Tua:</span>
                  <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.2 rounded">
                    Format Standar FinTech
                  </span>
                </div>
                <div className="bg-slate-900 text-emerald-400 p-3 rounded-lg font-mono text-[11px] whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
{`*BUKTI PEMBAYARAN TAGIHAN SEKOLAH*
*${waConfig.schoolName}*
------------------------------
Assalamu'alaikum Warahmatullahi Wabarakatuh
Yth. Orang Tua / Wali dari ananda [Nama Siswa] ([Kelas])

Alhamdulillah, pembayaran telah BERHASIL:
• No. Kuitansi : KWT-YYYYMMDD-XXXX
• Nominal      : Rp [Total Bayar]
• Metode Bayar : QRIS / Virtual Account / Kasir
• Status       : LUNAS (BERHASIL)

Rincian:
• SPP & Kegiatan Siswa Terbayar

${waConfig.footerNote}`}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  disabled={isWaSaving}
                  onClick={async () => {
                    setIsWaSaving(true);
                    try {
                      await api.updateWhatsAppConfig(waConfig);
                      showAlert('Pengaturan WhatsApp Gateway berhasil disimpan!', { type: 'success' });
                    } catch (e: any) {
                      showAlert(`Gagal menyimpan: ${e.message}`, { type: 'danger' });
                    } finally {
                      setIsWaSaving(false);
                    }
                  }}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <CheckCheck className="w-4 h-4" />
                  <span>{isWaSaving ? 'Menyimpan...' : 'Simpan Konfigurasi WhatsApp'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Webhook Logs Visualizer */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-3.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <span className="font-bold text-xs text-slate-900">Riwayat Webhook IPN Callback Realtime</span>
              <span className="text-[10px] text-slate-400 font-mono">Auto-Audit Trail</span>
            </div>
            <div className="p-3 space-y-2 max-h-64 overflow-y-auto text-xs">
              {simulationLogs.map((log, idx) => (
                <div key={idx} className="p-2.5 rounded border border-slate-200 bg-slate-50 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sky-800">{log.gateway}</span>
                      <span className="font-mono text-slate-600 font-semibold">{log.orderId}</span>
                      <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.2 rounded">
                        Rp {Number(log.amount).toLocaleString('id-ID')}
                      </span>
                      {log.issuer && (
                        <span className="text-[10px] bg-purple-100 text-purple-800 font-bold px-1.5 py-0.2 rounded">
                          via {log.issuer}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5">Waktu: {log.timestamp}</p>
                  </div>
                  <span className="text-emerald-700 font-bold text-xs flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>SETTLED</span>
                  </span>
                </div>
              ))}
              {simulationLogs.length === 0 && (
                <p className="text-center py-6 text-slate-400 text-xs">
                  Belum ada event webhook. Jalankan simulator di sebelah kanan untuk menguji!
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Right Col: Interactive Webhook Simulator */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-4">
            <h3 className="font-bold text-sm text-slate-900 border-b pb-2 flex items-center gap-2">
              <Play className="w-4 h-4 text-emerald-600" />
              <span>
                {activeTab === 'QRIS'
                  ? 'Simulasi Scan & Bayar QRIS'
                  : `Simulasi Webhook IPN Callback (${activeTab})`}
              </span>
            </h3>
            <p className="text-xs text-slate-500">
              {activeTab === 'QRIS'
                ? 'Uji coba instan scan QRIS pelanggan menggunakan berbagai aplikasi bank & e-wallet untuk otomatisasi pelunasan tagihan.'
                : 'Kirim simulasi notifikasi pembayaran dari server gateway untuk memverifikasi auto-update tagihan siswa.'}
            </p>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">
                  {activeTab === 'QRIS' ? 'Nomor Order / Transaksi' : 'Merchant Order ID / Invoice'}
                </label>
                <input
                  type="text"
                  value={testMerchantOrderId}
                  onChange={(e) => setTestMerchantOrderId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md font-mono"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Nominal Pembayaran (Rp)</label>
                <input
                  type="number"
                  value={testAmount}
                  onChange={(e) => setTestAmount(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md font-mono"
                />
              </div>

              {activeTab === 'QRIS' && (
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Aplikasi Pembayar (Issuer):</label>
                  <select
                    value={qrisSimulatedIssuer}
                    onChange={(e) => setQrisSimulatedIssuer(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md bg-white font-medium"
                  >
                    <option value="BCA Mobile">BCA Mobile</option>
                    <option value="Livin Mandiri">Livin' Mandiri</option>
                    <option value="BRImo">BRImo</option>
                    <option value="Muamalat DIN">Bank Muamalat DIN</option>
                    <option value="GoPay">GoPay Indonesia</option>
                    <option value="Dana">DANA Indonesia</option>
                    <option value="OVO">OVO</option>
                    <option value="ShopeePay">ShopeePay</option>
                  </select>
                </div>
              )}

              {activeTab === 'DUITKU' && (
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Result Code Duitku</label>
                  <select
                    value={testResultCode}
                    onChange={(e) => setTestResultCode(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md bg-white font-medium"
                  >
                    <option value="00">00 - Sukses Lunas</option>
                    <option value="01">01 - Pending / Menunggu Pembayaran</option>
                    <option value="02">02 - Dibatalkan / Expired</option>
                  </select>
                </div>
              )}

              {activeTab === 'MIDTRANS' && (
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Transaction Status Midtrans</label>
                  <select className="w-full px-3 py-2 border rounded-md bg-white font-medium">
                    <option value="settlement">settlement (Lunas)</option>
                    <option value="pending">pending</option>
                    <option value="expire">expire</option>
                  </select>
                </div>
              )}

              {activeTab === 'WHATSAPP' && (
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Nomor WhatsApp Penerima (Wali Murid):</label>
                  <input
                    type="text"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md font-mono"
                    placeholder="Contoh: 081234567890"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    * Simulasi akan menguji dispatch notifikasi otomatis ke nomor ini.
                  </p>
                </div>
              )}

              <button
                disabled={isSimulating}
                onClick={async () => {
                  if (activeTab === 'QRIS') {
                    handleSimulateQris();
                  } else if (activeTab === 'DUITKU') {
                    handleSimulateDuitku();
                  } else if (activeTab === 'MIDTRANS') {
                    handleSimulateMidtrans();
                  } else if (activeTab === 'WHATSAPP') {
                    setIsSimulating(true);
                    try {
                      // Find any transaction to test send
                      const trxRes = await fetch('/api/payment/transactions');
                      const trxData = await trxRes.json();
                      const sampleTrx = trxData?.data?.[0];

                      if (sampleTrx) {
                        const sendRes = await api.sendWhatsAppReceipt(sampleTrx.id, testPhone);
                        setSimulationLogs((prev) => [
                          {
                            timestamp: new Date().toLocaleTimeString('id-ID'),
                            gateway: 'WHATSAPP',
                            orderId: sampleTrx.noKuitansi,
                            amount: sampleTrx.totalBayar,
                            status: 'SUCCESS',
                            issuer: waConfig.provider,
                            response: sendRes,
                          },
                          ...prev,
                        ]);
                        showAlert(`Notifikasi WhatsApp Kuitansi (${sampleTrx.noKuitansi}) berhasil dikirim ke ${testPhone}!`, { type: 'success' });
                      } else {
                        showAlert('Belum ada transaksi pembayaran. Silakan lakukan pembayaran di Loket Kasir terlebih dahulu.', { type: 'warning' });
                      }
                    } catch (e: any) {
                      showAlert(`Gagal simulasi WhatsApp: ${e.message}`, { type: 'danger' });
                    } finally {
                      setIsSimulating(false);
                    }
                  }
                }}
                className={`w-full py-2.5 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-xs transition cursor-pointer ${
                  activeTab === 'QRIS'
                    ? 'bg-rose-600 hover:bg-rose-500'
                    : activeTab === 'WHATSAPP'
                    ? 'bg-emerald-600 hover:bg-emerald-500'
                    : 'bg-sky-700 hover:bg-sky-600'
                }`}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSimulating ? 'animate-spin' : ''}`} />
                <span>
                  {isSimulating
                    ? 'Mengirim & Verifikasi...'
                    : activeTab === 'QRIS'
                    ? `Simulasikan Scan & Bayar (${qrisSimulatedIssuer})`
                    : activeTab === 'WHATSAPP'
                    ? 'Uji Kirim Pesan WhatsApp Kuitansi'
                    : `Kirim Webhook ${activeTab}`}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
